import { useQueries, UseQueryResult } from '@tanstack/react-query';

import {
  BankDossierError,
  BankDossierLookup,
  buildDossierLookup,
  getBankDossiers,
  isBankDossierApiEnabled,
  mergeDossierLookups,
} from '../api/workOrders/bankDossier';
import { queryClient } from '../lib/queryClient';

export const bankDossierKeys = {
  all: ['bank-dossiers'] as const,
  batch: (ids: number[]) => [...bankDossierKeys.all, ids] as const,
};

const fetchLookup = (
  ids: number[],
  signal?: AbortSignal
): Promise<BankDossierLookup> =>
  getBankDossiers(ids, signal).then((responses) =>
    buildDossierLookup(ids, responses)
  );

// A refused session or role will not change on a retry.
const retry = (failureCount: number, error: Error): boolean =>
  !(error instanceof BankDossierError && [401, 403].includes(error.status)) &&
  failureCount < 1;

/** Same cache as the hook, for pages loaded outside react-query. */
export const fetchBankDossierLookup = (
  ids: number[]
): Promise<BankDossierLookup> =>
  queryClient.fetchQuery({
    queryKey: bankDossierKeys.batch(ids),
    queryFn: ({ signal }) => fetchLookup(ids, signal),
    retry,
  });

export const invalidateBankDossiers = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: bankDossierKeys.all });

// Module level so `useQueries` only recombines when a result changes.
const combine = (
  results: UseQueryResult<BankDossierLookup>[]
): BankDossierLookup | undefined =>
  mergeDossierLookups(results.map((result) => result.data));

/**
 * Bank dossiers of the work orders already on screen: one batch call per page
 * of the control center list, started once that page arrived. The rows never
 * wait for it; a failure leaves the dossier columns empty.
 */
export function useBankDossierLookup(
  idPages: number[][]
): BankDossierLookup | undefined {
  return useQueries({
    queries: idPages.map((ids) => ({
      queryKey: bankDossierKeys.batch(ids),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchLookup(ids, signal),
      enabled: isBankDossierApiEnabled && ids.length > 0,
      retry,
    })),
    combine,
  });
}
